import React from 'react';
import { CheckCircle, XCircle, CreditCard, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

export const PaymentSuccess = ({ orderId, onBackToHome }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('checkout.paymentSuccess', 'Payment Successful!')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('checkout.paymentSuccessMessage', 'Thank you for your purchase. Your order has been confirmed.')}
        </p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-500">{t('checkout.orderId', 'Order ID')}</p>
          <p className="font-mono font-bold text-gray-900">{orderId}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-2" />
          <p className="text-sm text-green-800">
            {t('checkout.confirmationEmail', 'A confirmation email has been sent to your email address.')}
          </p>
        </div>
        <Button onClick={onBackToHome} className="w-full bg-blue-600 hover:bg-blue-700">
          {t('checkout.backToHome', 'Back to Home')}
        </Button>
      </div>
    </div>
  );
};

export const PaymentFailed = ({ orderId, onRetry, onBackToHome }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('checkout.paymentFailed', 'Payment Failed')}
        </h2>
        <p className="text-gray-600 mb-4">
          {t('checkout.paymentFailedMessage', 'Unfortunately, your payment could not be processed. Please try again.')}
        </p>
        {orderId && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-500">{t('checkout.orderId', 'Order ID')}</p>
            <p className="font-mono font-bold text-gray-900">{orderId}</p>
          </div>
        )}
        <div className="flex gap-3">
          <Button onClick={onRetry} variant="outline" className="flex-1">
            {t('checkout.tryAgain', 'Try Again')}
          </Button>
          <Button onClick={onBackToHome} className="flex-1 bg-blue-600 hover:bg-blue-700">
            {t('checkout.backToHome', 'Back to Home')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PaymentFrame = ({ paymentToken, iframeRef, onBackToShop }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8 max-w-3xl">
        <button onClick={onBackToShop} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-6">
          <ArrowLeft className="w-4 h-4" />
          {t('checkout.backToCart', 'Back to Cart')}
        </button>
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t('checkout.completePayment', 'Complete Your Payment')}
          </h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <iframe
              ref={iframeRef}
              id="paytriframe"
              src={`https://www.paytr.com/odeme/guvenli/${paymentToken}`}
              frameBorder="0"
              scrolling="no"
              style={{ width: '100%', minHeight: '400px' }}
              title="PayTR Payment Form"
            />
          </div>
          <div className="mt-4 flex flex-col items-center justify-center gap-2 text-sm text-gray-500">
            <img
              src="/paytr-badge.png"
              alt="PayTR Güvenli Öde – Visa, Mastercard, Troy"
              className="h-12 w-auto"
              data-testid="paytr-logo-payment"
            />
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span>{t('checkout.securedBy', 'Secured by PayTR')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
